import React, { cloneElement } from 'react';
import { FileStack, ArrowRight as ArrowRightIcon, Globe, BarChart2, RefreshCw, Smartphone, Route, Network, Brain, RotateCw, XIcon, CheckIcon } from 'lucide-react';
interface FlowItem {
  todayIcon: React.ReactNode;
  todayLabel: string;
  todayMicro: string;
  snakkerIcon: React.ReactNode;
  snakkerLabel: string;
  snakkerMicro: string;
}
export function SlideComparisonStatic() {
  const items: FlowItem[] = [{
    todayIcon: <FileStack className="w-5 h-5" data-id="element-127" />,
    todayLabel: 'Static documents',
    todayMicro: 'Built to show — not engage',
    snakkerIcon: <Smartphone className="w-5 h-5" data-id="element-128" />,
    snakkerLabel: 'Interactive Snaks',
    snakkerMicro: 'Built to engage and learn'
  }, {
    todayIcon: <ArrowRightIcon className="w-5 h-5" data-id="element-129" />,
    todayLabel: 'Single push',
    todayMicro: 'One message, no progression',
    snakkerIcon: <Route className="w-5 h-5" data-id="element-130" />,
    snakkerLabel: 'Multi-step journeys',
    snakkerMicro: 'Adaptive, sequential flow'
  }, {
    todayIcon: <Globe className="w-5 h-5" data-id="element-131" />,
    todayLabel: 'Rework per market',
    todayMicro: 'Slow and expensive',
    snakkerIcon: <Network className="w-5 h-5" data-id="element-132" />,
    snakkerLabel: 'Master → Local',
    snakkerMicro: 'One source, many markets'
  }, {
    todayIcon: <BarChart2 className="w-5 h-5" data-id="element-133" />,
    todayLabel: 'Opens & clicks',
    todayMicro: 'No behavioral depth',
    snakkerIcon: <Brain className="w-5 h-5" data-id="element-134" />,
    snakkerLabel: 'Behavioral insight',
    snakkerMicro: 'What drives decisions'
  }, {
    todayIcon: <RefreshCw className="w-5 h-5" data-id="element-135" />,
    todayLabel: 'Late reports',
    todayMicro: 'Never applied',
    snakkerIcon: <RotateCw className="w-5 h-5" data-id="element-136" />,
    snakkerLabel: 'Continuous loop',
    snakkerMicro: 'Always improving'
  }];
  return <div className="h-full w-full flex flex-col bg-white text-gray-900 px-8 py-8 md:px-12 md:py-10 lg:px-16 lg:py-12 max-w-7xl mx-auto" data-id="element-137">
      {/* Header */}
      <h2 className="text-4xl md:text-5xl font-serif leading-tight text-gray-900 mb-2 text-center" data-id="element-138">
        From friction <span className="text-teal-600" data-id="element-139">→</span> to flow
      </h2>
      <p className="text-center text-gray-400 text-base mb-8" data-id="element-140">
        Not new work — better connected work
      </p>

      {/* Two-column layout */}
      <div className="flex-1 flex flex-col justify-center" data-id="element-141">
        <div className="grid grid-cols-2 gap-8 w-full max-w-6xl mx-auto" data-id="element-142">
          {/* Today Column */}
          <div data-id="element-143">
            <div className="flex items-center gap-2.5 mb-5 px-1" data-id="element-144">
              <div className="w-3.5 h-3.5 rounded-full bg-red-400/50" data-id="element-145" />
              <span className="text-base font-semibold text-gray-400 uppercase tracking-wider" data-id="element-146">
                Today
              </span>
            </div>
            <div className="space-y-3.5" data-id="element-147">
              {items.map((item, idx) => <div key={idx} className="flex items-center gap-5 rounded-2xl px-6 py-5 border border-gray-200 bg-gray-50/80" data-id="element-148">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 bg-gray-100 text-gray-400" data-id="element-149">
                    {cloneElement(item.todayIcon as React.ReactElement, {
                  className: 'w-6 h-6'
                })}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-150">
                    <span className="font-semibold text-base text-gray-700 block" data-id="element-151">
                      {item.todayLabel}
                    </span>
                    <span className="text-sm text-gray-400" data-id="element-152">
                      {item.todayMicro}
                    </span>
                  </div>
                  <XIcon className="w-5 h-5 text-red-400/60 flex-shrink-0" data-id="element-153" />
                </div>)}
            </div>
          </div>

          {/* Snakker Column */}
          <div data-id="element-154">
            <div className="flex items-center gap-2.5 mb-5 px-1" data-id="element-155">
              <div className="w-3.5 h-3.5 rounded-full bg-teal-500" data-id="element-156" />
              <span className="text-base font-semibold text-teal-600 uppercase tracking-wider" data-id="element-157">
                Snakker
              </span>
            </div>
            <div className="space-y-3.5" data-id="element-158">
              {items.map((item, idx) => <div key={idx} className="flex items-center gap-5 rounded-2xl px-6 py-5 border border-teal-200 bg-teal-50/60" data-id="element-159">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 bg-teal-100 text-teal-600" data-id="element-160">
                    {cloneElement(item.snakkerIcon as React.ReactElement, {
                  className: 'w-6 h-6'
                })}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-161">
                    <span className="font-semibold text-base text-gray-800 block" data-id="element-162">
                      {item.snakkerLabel}
                    </span>
                    <span className="text-sm text-gray-500" data-id="element-163">
                      {item.snakkerMicro}
                    </span>
                  </div>
                  <CheckIcon className="w-5 h-5 text-teal-500 flex-shrink-0" data-id="element-164" />
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </div>;
}